import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDateBR, formatMoneyBR } from "../../utils/format";

type BoletoImport = {
  id: number;
  status: string;
  lineDigitavel: string | null;
  barcode: string | null;
  amount: number | null;
  dueDate: string | null;
  beneficiaryName: string | null;
  extractionJson?: { debug?: { textLength?: number } } | null;
  errorsJson?: string[] | null;
};

export const BoletoImportPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [documentTypeSelected, setDocumentTypeSelected] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BoletoImport | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL as string;
  const token = useMemo(
    () => localStorage.getItem("mpf_admin_access_token"),
    []
  );

  const handleSubmit = async () => {
    if (!file) {
      setError("Selecione um PDF.");
      return;
    }
    if (!documentTypeSelected) {
      setError("Selecione o tipo de documento.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentTypeSelected", documentTypeSelected);

      const response = await fetch(
        `${apiUrl}/api/boleto-imports/from-pdf`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: formData,
        }
      );

      const payload = (await response.json()) as {
        data?: BoletoImport;
        error?: string;
      };

      if (!response.ok) {
        throw new Error(payload.error || "Falha ao processar PDF.");
      }

      setResult(payload.data ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao importar boleto.");
    } finally {
      setLoading(false);
    }
  };

  const errorsList = Array.isArray(result?.errorsJson)
    ? result?.errorsJson
    : [];

  return (
    <div className="container py-4">
      <h1 className="mb-3">Importar boleto (PDF)</h1>
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Tipo de documento</label>
            <select
              className="form-select"
              value={documentTypeSelected}
              onChange={(event) => setDocumentTypeSelected(event.target.value)}
            >
              <option value="">Selecione</option>
              <option value="TAXAS_TAUBATE">Taxas Taubaté</option>
              <option value="BOLETO_CNAB">Boleto CNAB</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Arquivo PDF</label>
            <input
              type="file"
              className="form-control"
              accept="application/pdf"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Processando..." : "Processar"}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {result && (
        <div className="card shadow-sm">
          <div className="card-body">
            <h5>Resumo</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="text-muted">Linha digitavel</div>
                <div>{result.lineDigitavel ?? "-"}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">Codigo de barras</div>
                <div>{result.barcode ?? "-"}</div>
              </div>
              <div className="col-md-4">
                <div className="text-muted">Valor</div>
                <div>
                  {result.amount != null
                    ? formatMoneyBR(result.amount)
                    : "-"}
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-muted">Vencimento</div>
                <div>
                  {result.dueDate ? formatDateBR(result.dueDate) : "-"}
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-muted">Beneficiario</div>
                <div>{result.beneficiaryName ?? "-"}</div>
              </div>
            </div>

            {result.status === "ERROR" && errorsList.length > 0 && (
              <div className="alert alert-warning mt-3">
                <strong>Erros encontrados</strong>
                <ul className="mb-0">
                  {errorsList.map((item, index) => (
                    <li key={`${item}-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.extractionJson?.debug?.textLength != null &&
              result.extractionJson.debug.textLength < 50 && (
                <div className="alert alert-info mt-3">
                  PDF sem texto, necessario OCR.
                </div>
              )}

            <div className="mt-3">
              <Link
                className="btn btn-outline-primary"
                to={`/financeiro/boletos/importar/${result.id}`}
              >
                Ver detalhes
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
